import { Alert, Box, Button, Flex, Spinner, Stack, Text } from '@chakra-ui/react'
import { LuDownload, LuRefreshCw } from 'react-icons/lu'
import { useCallback, useEffect, useRef, useState } from 'react'

import { getContractSettings } from '@/api/settings.api'
import {
  requestUserDataExport,
  downloadUserDataExport,
  subscribeToUserDataExportStatus,
} from '@/api/gdpr.api'
import type { ContractSettingsDTO } from '@/api/dto/settings.dto'
import type { UserDataExportDTO } from '@/api/dto/gdpr.dto'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { triggerBlobDownload } from '@/helper/common.helper'
import { AccountStatusBanner } from '@/components/settings/AccountStatusBanner'
import { Tooltip } from '@/components/ui/tooltip'
import { FormLayout } from '@/components/layout/FormLayout'

const EXPORT_NOT_ELIGIBLE_MESSAGE =
  'Der Datenexport ist erst verfügbar, sobald Ihr Abonnement gekündigt oder abgelaufen ist.'

function isExportEligible(contract: ContractSettingsDTO | null): boolean {
  if (!contract) return false
  if (contract.subscriptionStatus === 'CANCELLED' || contract.subscriptionStatus === 'EXPIRED') {
    return true
  }
  if (
    contract.accountStatus === 'EXPIRED' ||
    contract.accountStatus === 'GRACE_PERIOD' ||
    contract.accountStatus === 'NO_SUBSCRIPTION'
  ) {
    return true
  }
  if (contract.endDate) {
    const end = new Date(contract.endDate)
    if (!isNaN(end.getTime()) && end.getTime() < Date.now()) return true
  }
  return false
}

type StatusState =
  | { type: 'idle' }
  | { type: 'success'; message: string }
  | { type: 'error'; message: string }

function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error) return error.message
  return undefined
}

function formatDateTime(dateStr?: string | null) {
  if (!dateStr) return ''

  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr

  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d)
}

export function GdprExportPage() {
  const [contractLoading, setContractLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [contract, setContract] = useState<ContractSettingsDTO | null>(null)

  // null = no export requested yet / no status event received so far
  const [exportEvent, setExportEvent] = useState<UserDataExportDTO | null>(null)
  const [statusStreamUnavailable, setStatusStreamUnavailable] = useState(false)

  const [actionPending, setActionPending] = useState(false)
  const [status, setStatus] = useState<StatusState>({ type: 'idle' })

  const closeStreamRef = useRef<(() => void) | null>(null)

  const startStatusStream = useCallback(() => {
    closeStreamRef.current?.()
    closeStreamRef.current = subscribeToUserDataExportStatus({
      onEvent: event => {
        setStatusStreamUnavailable(false)
        setExportEvent(event)
      },
      onError: error => {
        console.error('user-data-export status stream error', error)
        setStatusStreamUnavailable(true)
      },
    })
  }, [])

  useEffect(() => {
    let mounted = true

    ;(async () => {
      setContractLoading(true)
      try {
        const dto = await getContractSettings()
        if (!mounted) return
        setContract(dto)
      } catch (error) {
        console.error(error)
        if (!mounted) return
        setLoadError('Fehler beim Laden der Vertragsdaten. Bitte versuchen Sie es später erneut.')
      } finally {
        if (mounted) setContractLoading(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    startStatusStream()
    return () => closeStreamRef.current?.()
  }, [startStatusStream])

  const handleRequestExport = async () => {
    try {
      setActionPending(true)
      setStatus({ type: 'idle' })

      const response = await requestUserDataExport()
      setExportEvent(response)
      // a previous stream may have already closed after a terminal event - restart it
      // so we keep receiving live updates for this new request.
      startStatusStream()

      setStatus({
        type: 'success',
        message: 'Ihr Datenexport wurde angefordert. Der Status wird automatisch aktualisiert.',
      })
    } catch (error) {
      console.error(error)
      const message =
        getErrorMessage(error) ||
        'Fehler beim Anfordern des Datenexports. Bitte versuchen Sie es später erneut.'
      setStatus({ type: 'error', message })
      toast.error(message)
    } finally {
      setActionPending(false)
    }
  }

  const handleDownloadExport = async () => {
    try {
      setActionPending(true)
      setStatus({ type: 'idle' })

      const { blob, filename } = await downloadUserDataExport()
      triggerBlobDownload(blob, filename || 'datenexport.zip')
    } catch (error) {
      console.error(error)
      const message =
        getErrorMessage(error) ||
        'Fehler beim Herunterladen des Datenexports. Bitte versuchen Sie es später erneut.'
      setStatus({ type: 'error', message })
      toast.error(message)
    } finally {
      setActionPending(false)
    }
  }

  if (contractLoading) {
    return (
      <Flex justify="center" py="16">
        <Spinner />
      </Flex>
    )
  }

  const exportStatus = exportEvent?.status ?? null
  const exportEligible = isExportEligible(contract)

  return (
    <FormLayout.Root>
      <AccountStatusBanner
        accountStatus={contract?.accountStatus}
        gracePeriodEndsAt={contract?.gracePeriodEndsAt}
      />

      {loadError && (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Description>{loadError}</Alert.Description>
        </Alert.Root>
      )}

      <FormLayout.Section data-testid="gdpr-export-settings">
        <FormLayout.Title>Datenexport (DSGVO)</FormLayout.Title>

        <FormLayout.Description>
          Hier können Sie eine vollständige Kopie Ihrer in der SIB-Software gespeicherten Daten
          anfordern (Stammdaten, Aktivitäten, Rechnungen sowie Vertrags- und Preisdaten). Dieser
          Export ist von den regulären Berichts-Exporten getrennt und dient ausschließlich der
          DSGVO-konformen Bereitstellung Ihrer persönlichen Daten, insbesondere vor einer möglichen
          Löschung Ihres Accounts.
        </FormLayout.Description>

        {!exportEligible && (
          <Alert.Root status="info">
            <Alert.Indicator />
            <Alert.Description>{EXPORT_NOT_ELIGIBLE_MESSAGE}</Alert.Description>
          </Alert.Root>
        )}

        {statusStreamUnavailable && !exportStatus && (
          <Alert.Root status="warning">
            <Alert.Indicator />
            <Alert.Description>
              Der Datenexport-Status konnte nicht abgerufen werden. Sie können trotzdem einen
              Export anfordern.
            </Alert.Description>
          </Alert.Root>
        )}

        <Stack gap={4} align="flex-start">
          {!exportStatus && (
            <FormLayout.Description>
              Sie haben noch keinen Datenexport angefordert.
            </FormLayout.Description>
          )}

          {(exportStatus === 'PENDING' || exportStatus === 'PROCESSING') && (
            <Flex align="center" gap={3}>
              <Spinner />
              <Text>
                Ihr Datenexport wird vorbereitet
                {exportEvent?.requestedAt
                  ? ` (angefordert am ${formatDateTime(exportEvent.requestedAt)})`
                  : ''}
                …
              </Text>
            </Flex>
          )}

          {exportStatus === 'COMPLETED' && (
            <Stack gap={1}>
              <Text>Ihr Datenexport ist bereit zum Download.</Text>
              {exportEvent?.completedAt && (
                <Text>
                  Fertiggestellt am {formatDateTime(exportEvent.completedAt)}.
                </Text>
              )}
              {exportEvent?.expiresAt && (
                <Text>
                  Verfügbar bis {formatDateTime(exportEvent.expiresAt)}.
                </Text>
              )}
            </Stack>
          )}

          {exportStatus === 'FAILED' && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Description>
                Bei der Erstellung Ihres Datenexports ist ein Fehler aufgetreten.
              </Alert.Description>
            </Alert.Root>
          )}

          <FormLayout.Actions>
            {exportStatus === 'COMPLETED' && exportEvent?.downloadAvailable && (
              <Button variant="subtle" loading={actionPending} onClick={handleDownloadExport}>
                <LuDownload />
                Export herunterladen
              </Button>
            )}

            {(!exportStatus || exportStatus === 'FAILED' || exportStatus === 'COMPLETED') && (
              <Tooltip content={EXPORT_NOT_ELIGIBLE_MESSAGE} disabled={exportEligible}>
                <Box as="span" display="inline-block">
                  <Button
                    variant="subtle"
                    loading={actionPending}
                    disabled={!exportEligible}
                    onClick={handleRequestExport}
                  >
                    <LuRefreshCw />
                    Datenexport anfordern
                  </Button>
                </Box>
              </Tooltip>
            )}
          </FormLayout.Actions>
        </Stack>

        {status.type !== 'idle' && (
          <Alert.Root status={status.type}>
            <Alert.Indicator />
            <Alert.Description>{status.message}</Alert.Description>
          </Alert.Root>
        )}
      </FormLayout.Section>

      {contract?.deletionBlockedReason && (
        <FormLayout.Section>
          <FormLayout.Title>Kontolöschung</FormLayout.Title>
          <FormLayout.Description>{contract.deletionBlockedReason}</FormLayout.Description>
        </FormLayout.Section>
      )}
    </FormLayout.Root>
  )
}
